import React, {MouseEvent} from "react";
import {observer} from "mobx-react";
import {
    IBookingSearchResultRecordViewModel
} from "../../../../services/agency-booking-search/booking-search-result-record.view-model.interface";
import styled, {css} from "styled-components";
import {CardBox} from "../../../../basic-components/card/card.box";
import {PlaneHorizontalFilledIcon} from "../../../../assets/svg/plane-horizontal-filled.icon";
import {Station} from "../../../../services/stations/station.service.interface";
import {useServices} from "../../../../hooks/use-services.hook";
import {EditPencilIcon} from "../../../../assets/svg/edit-pencil.icon";
import {BookingStatusEnum} from "../../../../services/dot-rez-api/data-contracts/enums/booking-status.enum";
import {
    IBookingSearchStrategyViewModel
} from "../../../../services/agency-booking-search/strategies/booking-search.strategy.interface";

const ItemContainerBox = styled(CardBox)<{$isSelected: boolean}>`
    display: grid;
    grid-template-columns: 2fr 1fr;
    cursor: pointer;
    row-gap: ${props => props.theme.spacing.flexGap12};
    column-gap: ${props => props.theme.spacing.flexGap20};
    align-items: center;
    font-size: ${props => props.theme.fontSize.body2};
    padding-top: ${props => props.theme.spacing.spacing12};
    box-shadow: ${props => props.theme.boxShadow.shadow2};
    
    ${props => props.$isSelected && css`
        background-color: ${props => props.theme.colors.primary};
        color: ${props => props.theme.colors.primaryContrast};
    `}
    
    &:hover {
        box-shadow: none;
    }
`

const ModifyButtonBox = styled.div<{$isSelected: boolean}>`
    grid-column: 1/-1;
    width: 100%;
    top: 8px;
    right: ${props => props.theme.spacing.spacing12};
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    cursor: pointer;
    text-decoration: underline;
    font-size: ${props => props.theme.fontSize.h6};
    font-weight: ${props => props.theme.fontWeight.semiBold};
    
    ${props => !props.$isSelected && 
            css`
                color: ${props => props.theme.colors.mediumShade};
            `
    }

    &:hover {
        color: ${props => props.theme.colors.dark};
    }
`

const OriginAndDestinationBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${props => props.theme.spacing.flexGap16};
`

const StationBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`

const StationCodeBox = styled.div`
    display: flex;
    flex-direction: row;
    font-size: ${props => props.theme.fontSize.h1};
    font-weight: ${props => props.theme.fontWeight.bold};
`



const FieldBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`
const FieldValueBox = styled.div`
    font-size: ${props => props.theme.fontSize.h5};
    font-weight: ${props => props.theme.fontWeight.medium};
`

const FieldLabelBox = styled.div<{$isSelected: boolean}>`
    font-size: ${props => props.theme.fontSize.subtitle2};
    ${props => props.$isSelected 
            ? css`
                color: ${props => props.theme.colors.lightShade};
            `
            : css`
                color: ${props => props.theme.colors.notesShade};
            `
    }
`

const StationNameBox = styled(FieldLabelBox)`
    text-align: center;
`

const RowDividerBox = styled.div`
    width: 100%;
    height: 1px;
    background-color: ${props => props.theme.border.mainColor};
    grid-column: 1/-1;
`

const StationComponent: React.FC<{station: Station; isSelected: boolean}> = observer((props) => {
    return (
        <StationBox>
            <StationCodeBox>{props.station.stationCode}</StationCodeBox>
            <StationNameBox $isSelected={props.isSelected}>
                {props.station.stationName}
            </StationNameBox>
        </StationBox>
    )
})

const BookingStatusHoldBox = styled(FieldValueBox)`
    color: ${props => props.theme.colors.dangerShade};
    font-weight: ${props => props.theme.fontWeight.bold};
`

const BookingStatusConfirmedBox = styled(FieldValueBox)<{$isSelected: boolean}>`
    ${props => !props.$isSelected && css`
        color: ${props => props.theme.colors.successShade};
    `}
    
`

const BookingStatusFieldComponent: React.FC<{record: IBookingSearchResultRecordViewModel; isSelectedRecord: boolean}> = observer((props) => {
    if(props.record.bookingStatus === BookingStatusEnum.Hold) {
        return (
            <BookingStatusHoldBox>
                {props.record.bookingStatusName}
            </BookingStatusHoldBox>
        )
    }

    if(props.record.bookingStatus === BookingStatusEnum.Confirmed) {
        return (
            <BookingStatusConfirmedBox $isSelected={props.isSelectedRecord}>
                {props.record.bookingStatusName}
            </BookingStatusConfirmedBox>
        )
    }

    return (
        <FieldLabelBox $isSelected={props.isSelectedRecord}>{props.record.bookingStatusName}</FieldLabelBox>
    )
})

interface CardFieldComponentProps {
    label: string;
    value: string;
    isSelectedItem: boolean;
}

const CardFieldComponent: React.FC<CardFieldComponentProps> = observer((props) => {
    return (
        <FieldBox>
            <FieldLabelBox $isSelected={props.isSelectedItem}>{props.label}</FieldLabelBox>
            <FieldValueBox>{props.value}</FieldValueBox>
        </FieldBox>
    )
})

interface BookingSearchResultItemCardComponentProps {
    record: IBookingSearchResultRecordViewModel;
    currentStrategy: IBookingSearchStrategyViewModel;
}

export const BookingSearchResultRecordCardViewComponent: React.FC<BookingSearchResultItemCardComponentProps> = observer((props) => {
    const services = useServices();

    const isSelectedItem = props.record.uniqueKey === props.currentStrategy.selectedRecordKey;

    const renderOriginAndDestination = () => {

        if(!props.record.origin || !props.record.destination) {
            return null;
        }

        return (
            <>
                <StationComponent station={props.record.origin} isSelected={isSelectedItem}/>
                <PlaneHorizontalFilledIcon/>
                <StationComponent station={props.record.destination} isSelected={isSelectedItem}/>
            </>
        );

    }

    const renderModifyButton = () => {
        if(!props.record.canBeModified) {
            return null;
        }

        const onModifyButtonClick = async (event: MouseEvent<HTMLDivElement>) => {
            if(isSelectedItem) {
                event.stopPropagation();
                event.preventDefault();
            }
            await props.record.retrieveBooking();
        }

        return (
            <ModifyButtonBox onClick={onModifyButtonClick} $isSelected={isSelectedItem}>
                <EditPencilIcon size={0.8}/>
                <span>
                    {services.language.translate('Modify')}
                </span>
            </ModifyButtonBox>
        );
    }

    const renderFlightDate = () => {
        if(props.record.flightDepartureDate) {
            return `${props.record.flightDepartureDate} ${props.record.flightDepartureTime}`;
        }
        return "N/A";
    }

    const onCardClick = () => {
        if( props.currentStrategy.selectedRecordKey === props.record.uniqueKey) {
            props.currentStrategy.selectedRecordKey = null;
        } else {
            props.currentStrategy.selectedRecordKey = props.record.uniqueKey;
        }

    }

    return (
        <ItemContainerBox $isSelected={isSelectedItem} onClick={onCardClick}>
            {renderModifyButton()}
            <OriginAndDestinationBox>
                {renderOriginAndDestination()}
            </OriginAndDestinationBox>


            <FieldBox>
                <FieldValueBox>{props.record.pnr}</FieldValueBox>
                <BookingStatusFieldComponent record={props.record} isSelectedRecord={isSelectedItem}/>
            </FieldBox>

            <RowDividerBox/>

            <CardFieldComponent isSelectedItem={isSelectedItem}
                                label={services.language.translate('Flight date')}
                                value={renderFlightDate()}/>

            <CardFieldComponent isSelectedItem={isSelectedItem}
                                label={services.language.translate('Flight number')}
                                value={props.record.flightNumber || "N/A"}/>


            <RowDividerBox/>

            <CardFieldComponent isSelectedItem={isSelectedItem}
                                label={services.language.translate('Passenger')}
                                value={props.record.passenger}/>

            <CardFieldComponent isSelectedItem={isSelectedItem}
                                label={services.language.translate('Agent')}
                                value={props.record.agent}/>


        </ItemContainerBox>
    )
})